import api from './api';

export interface BasicFSIResponse {
  // Old structure for backward compatibility
  gid?: number;
  plot_area?: number;
  fsi_value?: number;
  max_construction_area?: number;
  zone_type?: string;
  road_width?: number;
  analysis_date?: string;
  
  // New structure matching API response
  status?: string;
  basic_fsi_analysis?: {
    calculation_metadata?: {
      calculated_at?: string;
      calculation_type?: string;
      gid?: number;
      sla_version?: string;
      spatial_road_analysis_used?: boolean;
    };
    financial_estimates?: {
      construction?: {
        cost_per_sqm?: number;
        gross_buildable_sqm?: number;
        total_cost?: number;
      };
      infrastructure?: {
        cost_per_sqm?: number;
        total_cost?: number;
      };
      misc_costs?: number;
      profitability?: {
        cost_per_saleable_sqm?: number;
        gross_profit?: number;
        margin_percentage?: number;
        roi_percentage?: number;
      };
      revenue?: {
        sale_rate_per_sqm?: number;
        saleable_area_sqm?: number;
        saleable_ratio?: number;
        total_revenue?: number;
      };
      sla_reference?: string;
      statutory_charges?: {
        additional_fsi_charges?: number;
        betterment_charges?: number;
        scrutiny_fees?: number;
        total_statutory?: number;
      };
      total_development_cost?: number;
    };
    flags_and_warnings?: Array<{
      action?: string;
      message?: string;
      severity?: string;
      type?: string;
    }>;
    fp_information?: {
      area_acres?: number;
      area_sqm?: number;
      district?: string;
      fps_code?: string;
      gid?: number;
      land_use_purpose?: string | null;
      land_use_type?: string | null;
      name?: string;
      state?: string;
      status?: string;
      tps_id?: number;
      village?: string;
    };
    fsi_calculations?: {
      applied_fsi?: number;
      base_fsi?: number;
      development_efficiency_pct?: number;
      gross_buildable_area_sqm?: number;
      gross_plot_area_sqm?: number;
      max_chargeable_fsi?: number;
      max_ground_floor_area_sqm?: number;
      maximum_buildable_area_sqm?: number;
      maximum_possible_fsi?: number;
      net_developable_area_sqm?: number;
      premium_fsi?: number;
      tp_deduction_area_sqm?: number;
      tp_deduction_pct?: number;
    };
    height_compliance?: {
      building_height_m?: number;
      consequence_class_code?: string;
      consequence_class_description?: string;
      cowor_required?: boolean;
      height_compliant?: boolean;
      height_excess_m?: number;
      max_height_allowed_m?: number;
      risk_level?: string;
      seor_requirement?: string;
      sla_reference?: string;
      special_approvals_needed?: boolean;
    };
    land_use_analysis?: {
      development_charges_applicable?: boolean;
      effective_base_fsi?: number;
      fsi_compliant?: boolean;
      fsi_source?: string;
      height_restrictions?: {
        max_height_m?: number | null;
      };
      land_use_compliance?: {
        compliant?: boolean;
        notes?: string[];
      };
      land_use_purpose?: string | null;
      land_use_type?: string;
      permitted_fsi_range?: number[];
      premium_applicable?: boolean;
      special_provisions?: string[];
    };
    overlays_analysis?: {
      airport_obstacle_zone?: boolean;
      environmental_restriction_zone?: boolean;
      heritage_precinct?: boolean;
      overlay_analysis_method?: string;
      railway_buffer_zone?: boolean;
      recommendations?: string[];
      risk_factors?: string[];
      river_setback_zone?: boolean;
    };
    recommendations?: string[];
    regulatory_requirements?: {
      additional_requirements?: string[];
      core_approvals_needed?: string[];
      scrutiny_fees?: {
        minimum_fee_rs?: number;
        rate_per_sqm?: number;
        total_fee_rs?: number;
      };
      sla_reference?: string;
      special_considerations?: string[];
      timeline_estimates?: {
        building_plan_approval_months?: number;
        completion_certificate_months?: number;
        construction_phase_months?: number;
        development_permission_months?: number;
        total_timeline_months?: number;
      };
    };
    road_analysis?: {
      premium_multiplier?: number;
      premium_percentage?: number;
      road_category?: string;
      road_width_ft?: number;
      road_width_m?: number;
      sla_reference?: string;
      source?: string;
      tall_building_eligible?: boolean;
    };
    spatial_road_analysis?: any;
    zone_analysis?: {
      base_fsi?: number;
      ground_coverage_pct?: number | null;
      has_fallback_zone?: boolean;
      land_use_override?: boolean;
      max_permissible_fsi?: number | null;
      plot_area_sqm?: number;
      requires_official_verification?: boolean;
      zone_code?: string;
      zone_constraints?: string[];
      zone_description?: string;
    };
    chargeable_fsi_analysis?: {
      applicable_schedule?: string;
      charge_rate_pct_of_jantri?: number;
      green_building_exemption_max_pct?: number;
      green_building_exemption_min_pct?: number;
      max_chargeable_fsi?: number;
      max_chargeable_fsi_pct?: number;
      road_width_impact?: string;
      sla_reference?: string;
    };
  };
  
  // Backward compatibility accessors
  fp_information?: {
    area_acres?: number;
    area_sqm?: number;
    district?: string;
    fps_code?: string;
    gid?: number;
    land_use_purpose?: string | null;
    land_use_type?: string | null;
    name?: string;
    state?: string;
    status?: string;
    tps_id?: number;
    village?: string;
  };
  fsi_calculations?: {
    applied_fsi?: number;
    base_fsi?: number;
    development_efficiency_pct?: number;
    gross_buildable_area_sqm?: number;
    gross_plot_area_sqm?: number;
    max_chargeable_fsi?: number;
    max_ground_floor_area_sqm?: number;
    maximum_buildable_area_sqm?: number;
    maximum_possible_fsi?: number;
    net_developable_area_sqm?: number;
    premium_fsi?: number;
    tp_deduction_area_sqm?: number;
    tp_deduction_pct?: number;
  };
  financial_estimates?: {
    construction?: {
      cost_per_sqm?: number;
      gross_buildable_sqm?: number;
      total_cost?: number;
    };
    infrastructure?: {
      cost_per_sqm?: number;
      total_cost?: number;
    };
    misc_costs?: number;
    profitability?: {
      cost_per_saleable_sqm?: number;
      gross_profit?: number;
      margin_percentage?: number;
      roi_percentage?: number;
    };
    revenue?: {
      sale_rate_per_sqm?: number;
      saleable_area_sqm?: number;
      saleable_ratio?: number;
      total_revenue?: number;
    };
    sla_reference?: string;
    statutory_charges?: {
      additional_fsi_charges?: number;
      betterment_charges?: number;
      scrutiny_fees?: number;
      total_statutory?: number;
    };
    total_development_cost?: number;
  };
  road_analysis?: {
    premium_multiplier?: number;
    premium_percentage?: number;
    road_category?: string;
    road_width_ft?: number;
    road_width_m?: number;
    sla_reference?: string;
    source?: string;
    tall_building_eligible?: boolean;
  };
  zone_analysis?: {
    base_fsi?: number;
    ground_coverage_pct?: number | null;
    has_fallback_zone?: boolean;
    land_use_override?: boolean;
    max_permissible_fsi?: number | null;
    plot_area_sqm?: number;
    requires_official_verification?: boolean;
    zone_code?: string;
    zone_constraints?: string[];
    zone_description?: string;
  };
  flags_and_warnings?: Array<{
    action?: string;
    message?: string;
    severity?: string;
    type?: string;
  }>;
  recommendations?: string[];
  
  // Additional backward compatibility properties
  height_compliance?: {
    building_height_m?: number;
    consequence_class_code?: string;
    consequence_class_description?: string;
    cowor_required?: boolean;
    height_compliant?: boolean;
    height_excess_m?: number;
    max_height_allowed_m?: number;
    risk_level?: string;
    seor_requirement?: string;
    sla_reference?: string;
    special_approvals_needed?: boolean;
  };
  calculation_metadata?: {
    calculated_at?: string;
    calculation_type?: string;
    gid?: number;
    sla_version?: string;
    spatial_road_analysis_used?: boolean;
  };
}

export interface DetailedFSIResponse {
  status: string;
  basic_fsi_analysis: BasicFSIResponse['basic_fsi_analysis'];
  detailed_sections: {
    financial_analysis: {
      detailed_costs: {
        construction: {
          area_sqm: number;
          rate_per_sqm: number;
          total_cost: number;
        };
        contingency: number;
        infrastructure: {
          rate_per_sqm: number;
          total_cost: number;
        };
        professional_fees: number;
        statutory_charges: {
          additional_fsi_cost: number;
          betterment_charges: number;
          scrutiny_fees: number;
          total: number;
        };
        total_development_cost: number;
      };
      profitability_metrics: {
        cost_per_saleable_sqm: number;
        gross_profit: number;
        profit_margin_pct: number;
        revenue_multiple: number;
        roi_pct: number;
      };
      revenue_analysis: {
        buildable_area_sqm: number;
        gross_revenue: number;
        market_rate_per_sqm: number;
        saleable_area_sqm: number;
        saleable_ratio: number;
      };
      sensitivity_analysis: {
        [key: string]: {
          cost_variation_pct: number;
          profit: number;
          rate_variation_pct: number;
          roi_pct: number;
        };
      };
    };
    fsi_analysis: BasicFSIResponse['basic_fsi_analysis'];
    plot_analysis: {
      area_acres: number;
      area_sqm: number;
      district: string;
      fps_code: string;
      gid: number;
      land_use_purpose: string | null;
      land_use_type: string | null;
      name: string;
      state: string;
      status: string;
      tps_id: number;
      village: string;
    };
    recommendations: Array<{
      actions: string[];
      category: string;
      priority: string;
    }>;
    regulatory_analysis: {
      additional_requirements: string[];
      core_approvals_needed: string[];
      scrutiny_fees: {
        minimum_fee_rs: number;
        rate_per_sqm: number;
        total_fee_rs: number;
      };
      sla_reference: string;
      special_considerations: string[];
      timeline_estimates: {
        building_plan_approval_months: number;
        completion_certificate_months: number;
        construction_phase_months: number;
        development_permission_months: number;
        total_timeline_months: number;
      };
    };
    risk_analysis: {
      identified_risks: Array<{
        category: string;
        description: string;
        impact: string;
        mitigation: string;
        severity: string;
      }>;
      mitigation_priority: string[];
      overall_risk_score: number;
      risk_assessment_summary: string;
      risk_categories: {
        [key: string]: number;
      };
      risk_level: string;
    };
    spatial_visualization: {
      adjacent_roads: Array<{
        geometry: {
          coordinates: number[][][];
          type: string;
        };
        properties: {
          distance_to_plot: number;
          is_adjacent: boolean;
          road_id: number;
          type: string;
          width_m: number;
        };
        type: string;
      }>;
      plot_polygon: {
        geometry: {
          coordinates: number[][][][];
          type: string;
        };
        properties: {
          area_acres: number;
          area_sqm: number;
          gid: number;
          land_use_type: string | null;
          tps_id: number;
          type: string;
          village: string;
        };
        type: string;
      };
      summary: {
        adjacent_roads_count: number;
        coordinate_system: string;
        plot_available: boolean;
        purpose: string;
        visualization_buffer_available: boolean;
      };
      visualization_buffer: {
        geometry: {
          coordinates: number[][][];
          type: string;
        };
        properties: {
          buffer_distance_m: number;
          purpose: string;
          type: string;
        };
        type: string;
      };
    };
  };
  development_scenarios: Array<{
    description: string;
    estimated_timeline_months: number;
    fsi_utilized: number;
    height_m: number;
    name: string;
    risk_level: string;
  }>;
  enhanced_financial_analysis: {
    detailed_costs: {
      construction: {
        area_sqm: number;
        rate_per_sqm: number;
        total_cost: number;
      };
      contingency: number;
      infrastructure: {
        rate_per_sqm: number;
        total_cost: number;
      };
      professional_fees: number;
      statutory_charges: {
        additional_fsi_cost: number;
        betterment_charges: number;
        scrutiny_fees: number;
        total: number;
      };
      total_development_cost: number;
    };
    profitability_metrics: {
      cost_per_saleable_sqm: number;
      gross_profit: number;
      profit_margin_pct: number;
      revenue_multiple: number;
      roi_pct: number;
    };
    revenue_analysis: {
      buildable_area_sqm: number;
      gross_revenue: number;
      market_rate_per_sqm: number;
      saleable_area_sqm: number;
      saleable_ratio: number;
    };
    sensitivity_analysis: {
      [key: string]: {
        cost_variation_pct: number;
        profit: number;
        rate_variation_pct: number;
        roi_pct: number;
      };
    };
  };
  executive_summary: {
    critical_factors: string[];
    investment_recommendation: {
      color: string;
      confidence_level: string;
      rating: string;
    };
    key_metrics: {
      applied_fsi: number;
      buildable_area_sqm: number;
      projected_profit: number;
      projected_roi_pct: number;
      risk_count: number;
      risk_level: string;
    };
    next_immediate_actions: string[];
  };
  overlay_analysis: {
    clearances_required: string[];
    development_constraints: string[];
    overlay_impact_summary: string;
    overlays_detected: {
      airport_obstacle_zone: {
        height_restriction_m: number | null;
        present: boolean;
      };
      environmental_clearance_zone: {
        clearance_required: boolean;
        present: boolean;
      };
      flood_zone: {
        flood_level_m: number | null;
        present: boolean;
      };
      heritage_precinct: {
        present: boolean;
        restrictions: string[];
      };
      railway_buffer: {
        distance_m: number | null;
        present: boolean;
      };
    };
  };
  plot_information: {
    area_acres: number;
    area_sqm: number;
    district: string;
    fps_code: string;
    gid: number;
    land_use_purpose: string | null;
    land_use_type: string | null;
    name: string;
    state: string;
    status: string;
    tps_id: number;
    village: string;
  };
  polygon_data: {
    adjacent_roads: Array<{
      geometry: {
        coordinates: number[][][];
        type: string;
      };
      properties: {
        distance_to_plot: number;
        is_adjacent: boolean;
        road_id: number;
        type: string;
        width_m: number;
      };
      type: string;
    }>;
    plot_polygon: {
      geometry: {
        coordinates: number[][][][];
        type: string;
      };
      properties: {
        area_acres: number;
        area_sqm: number;
        gid: number;
        land_use_type: string | null;
        tps_id: number;
        type: string;
        village: string;
      };
      type: string;
    };
    summary: {
      adjacent_roads_count: number;
      coordinate_system: string;
      plot_available: boolean;
      purpose: string;
      visualization_buffer_available: boolean;
    };
    visualization_buffer: {
      geometry: {
        coordinates: number[][][];
        type: string;
      };
      properties: {
        buffer_distance_m: number;
        purpose: string;
        type: string;
      };
      type: string;
    };
  };
  recommendations: Array<{
    actions: string[];
    category: string;
    priority: string;
  }>;
  report_metadata: {
    analysis_parameters: {
      building_height_m: number;
      construction_cost_per_sqm: number;
      include_financial_analysis: boolean;
      include_tall_building_analysis: boolean;
      jantri_rate_per_sqm: number;
      perform_road_analysis: boolean;
      road_width_m: number;
      sale_rate_per_sqm: number;
      tall_building_eligible: boolean;
      tp_deduction_pct: number;
      zone_code: string;
    };
    analysis_version: string;
    generated_at: string;
    gid: number;
    report_type: string;
    sla_version: string;
  };
  report_type: string;
  risk_assessment: {
    identified_risks: Array<{
      category: string;
      description: string;
      impact: string;
      mitigation: string;
      severity: string;
    }>;
    mitigation_priority: string[];
    overall_risk_score: number;
    risk_assessment_summary: string;
    risk_categories: {
      [key: string]: number;
    };
    risk_level: string;
  };
  road_analysis: {
    adjacent_roads: Array<{
      distance_to_fp: number;
      fsi_premium: number;
      full_road_geometry: {
        coordinates: number[][][];
        type: string;
      };
      geometry: {
        coordinates: number[][][];
        type: string;
      };
      is_adjacent: boolean;
      road_id: number;
      width_ft: number;
      width_m: number;
    }>;
    adjacent_roads_count: number;
    adjacent_roads_within_buffer: Array<{
      distance_to_fp: number;
      fsi_premium: number;
      full_road_geometry: {
        coordinates: number[][][];
        type: string;
      };
      geometry: {
        coordinates: number[][][];
        type: string;
      };
      is_adjacent: boolean;
      road_id: number;
      width_ft: number;
      width_m: number;
    }>;
    analysis_summary: {
      fp_area_sqm: number;
      method: string;
    };
    primary_road: {
      distance_to_fp: number;
      fsi_premium: number;
      is_adjacent: boolean;
      road_category: string;
      width_ft: number;
      width_m: number;
    };
    primary_road_info: {
      distance_to_fp: number;
      fsi_premium: number;
      is_adjacent: boolean;
      road_category: string;
      width_ft: number;
      width_m: number;
    };
    source: string;
    status: string;
    total_roads_found: number;
  };
  sba_analysis?: {
    area_data?: {
      fp_area_sqm?: number;
      net_developable_sqm?: number;
      op_area_sqm?: number;
      retention_pct?: number;
      tp_deduction_pct?: number;
    };
    assumptions?: {
      common_area_loading_pct?: number;
      coverage_pct?: number;
      saleable_ratio?: number;
      typical_floor_height_m?: number;
      unit_mix?: Array<{
        proportion: number;
        size_sqm: number;
        type: string;
      }>;
    };
    floor_analysis?: {
      building_height_m?: number;
      coverage_pct?: number;
      final_floorplate_sqm?: number;
      final_permissible_floors?: number;
      floorplate_by_coverage_sqm?: number;
      floors_by_fsi?: number;
      floors_by_height?: number;
      limiting_factor?: string;
      typical_floor_height_m?: number;
    };
    fsi_calculations?: {
      base_fsi?: number;
      fsi_premium_pct?: number;
      gross_buildable_sqm?: number;
      permitted_max_fsi?: number;
      zone_code?: string;
    };
    sba_calculations?: {
      SBA_per_floor_sqm?: number;
      SBA_sqm?: number;
      common_area_loading_pct?: number;
      common_area_sqm?: number;
      proportional_common_area_sqm?: number;
      saleable_area_sqm?: number;
      saleable_ratio?: number;
    };
    success?: boolean;
    unit_analysis?: {
      avg_unit_size_sqm?: number;
      total_units?: number;
      units_breakdown?: Array<{
        count: number;
        proportion_pct: number;
        size_sqm: number;
        total_saleable_sqm: number;
        type: string;
      }>;
      units_per_floor?: number;
    };
  };
  summary_statistics: {
    SBA_sqm?: number;
    applied_fsi: number;
    avg_unit_size_sqm?: number;
    buildable_area_sqm: number;
    development_cost: number;
    investment_viability: string;
    projected_roi: number;
    saleable_area_sqm?: number;
    total_floors?: number;
    total_units?: number;
  };
  tall_building_analysis: {
    eligibility_reason: string;
    height_scenarios: Array<{
      approximate_floors: number;
      buildable_area_sqm: number;
      consequence_class: string | null;
      estimated_development_cost: number;
      estimated_fsi: number;
      height_compliant: boolean;
      height_m: number;
      seor_requirement: string;
      special_approvals_needed: boolean;
      viability_rating: string;
    }>;
    minimum_road_width_required: number;
    plot_area_sqm: number;
    primary_road_width: number;
    recommendations: string[];
    road_width_eligible: boolean;
    tall_building_constraints: string[];
    widest_road_name: string;
    widest_road_width: number;
  };
}

export interface DetailedFSIOptions {
  include_financial_analysis?: boolean;
  include_gdrc_analysis?: boolean;
  include_road_analysis?: boolean;
  custom_parameters?: {
    construction_cost_per_sqm?: number;
    land_cost_per_sqm?: number;
    profit_margin?: number;
  };
}

export interface PlotInfo {
  gid: number;
  tps_id: string;
  plot_number: string;
  area: number;
  zone_type: string;
  geometry: any;
}

export interface BatchFSIRequest {
  gids: number[];
  include_geometry?: boolean;
  analysis_type: 'basic' | 'detailed';
}

export class FSIApiService {
  async getBasicFSI(gid: string, options?: { zoneCode?: string; roadWidth?: number }): Promise<BasicFSIResponse> {
    const params = new URLSearchParams();
    if (options?.zoneCode) {
      params.append('zone_code', options.zoneCode);
    }
    if (options?.roadWidth) {
      params.append('road_width_m', options.roadWidth.toString());
    }
    
    const queryString = params.toString();
    const url = queryString ? `/fsi/basic/${gid}?${queryString}` : `/fsi/basic/${gid}`;
    
    const response = await api.get(url);
    return response.data;
  }

  async getDetailedFSI(gid: string, options: DetailedFSIOptions = {}): Promise<DetailedFSIResponse> {
    const response = await api.post(`/fsi/detailed/${gid}`, options);
    return response.data;
  }

  async getDetailedFSIAnalysis(gid: string, options: DetailedFSIOptions = {}): Promise<DetailedFSIResponse> {
    const response = await api.post(`/fsi/detailed/${gid}`, options);
    return response.data;
  }

  // Legacy method name for backward compatibility
  async getDetailedReport(gid: string, options: DetailedFSIOptions = {}): Promise<DetailedFSIResponse> {
    return this.getDetailedFSI(gid, options);
  }

  async getPlotInfo(gid: string): Promise<PlotInfo> {
    const response = await api.get(`/fsi/info/${gid}`);
    return response.data;
  }

  async getBatchFSI(request: BatchFSIRequest): Promise<BasicFSIResponse[]> {
    const response = await api.post('/fsi/batch/basic', request);
    return response.data;
  }

  async getFSIZones(zoneType?: string) {
    const params = zoneType ? `?zone_type=${zoneType}` : '';
    const response = await api.get(`/fsi/zones${params}`);
    return response.data;
  }

  async getFSIParameters() {
    const response = await api.get('/fsi/parameters');
    return response.data;
  }

  async generatePDFReport(gid: string, format: 'comprehensive' | 'summary' = 'comprehensive', includeMaps = true) {
    const params = new URLSearchParams({
      format,
      include_maps: includeMaps.toString(),
    });
    const response = await api.get(`/fsi/pdf/${gid}?${params}`, {
      responseType: 'blob',
    });
    return response.data;
  }

  async generateBatchPDF(request: {
    gids: number[];
    format: 'comprehensive' | 'summary';
    include_maps?: boolean;
    include_financial_analysis?: boolean;
  }) {
    const response = await api.post('/fsi/batch/pdf', request, {
      responseType: 'blob',
    });
    return response.data;
  }
}

export const fsiService = new FSIApiService();
